import React from 'react'
import moment from 'moment'
import { date, cashmoneyhelper, NoContentBox, TableLoader } from '@enkla-gui'

const ACTION_VERBS = {
	select: 'Välj',
	edit: 'Redigera',
	remove: 'Ta bort',
	delete: 'Ta bort',
	open: 'Öppna',
	download: 'Ladda ner',
	show: 'Visa',
	detach: 'Koppla bort',
	pdf: 'PDF',
	unlink: 'Koppla bort'
}

export default function Table(props) {
	let rowKeys = props.keys

	if (Array.isArray(props.keys)) {
		rowKeys = props.keys.join('|')
	}
	const getValueFromPath = function(obj, path) {
		var stack = path.split('.')
		while (stack.length) {
			if (!obj) return null
			obj = obj[stack.shift()]
		}
		return obj
	}
	const getRowLabel = row => row?.name || row?.title || row?.identifier || 'rad'
	const getActionLabel = (actionKey, row, customLabel) => {
		if (customLabel) {
			return customLabel.replace(/@{([^}]+)}/gi, (match, property) => getValueFromPath(row, property) ?? '')
		}
		const verb = ACTION_VERBS[actionKey] || actionKey
		return `${verb} ${getRowLabel(row)}`
	}
	const onRowClick = row => {
		if (props.link) {
			window.location.href = getLinkHref(row, props.link)
		}
		if (props.onClick) {
			props.onClick(row)
		}
	}
	const getLinkHref = (row, link) => {
		return link.replace(/@{([^}]+)}/gi, (match, property) => getValueFromPath(row, property))
	}
	const parseKey = (value, key, row, x, y) => {
		let col = ''
		switch (key[0]) {
			case 'link':
				col = (
					<a
						key={`table_row_${x}_${y}_key_${key}`}
						href={getLinkHref(row, key[1])}
						onClick={e => e.stopPropagation()}
						class='btn btn-sm btn-light table-icon-action'
						aria-label={getActionLabel('open', row, key[3])}>
						<i class={`fas fa-fw ${key[2]}`} aria-hidden="true" />
					</a>
				)
				break
			case 'date':
				if (value === null || value === undefined) {
					col = ''
				} else if (typeof date[key[1]] === 'function') {
					col = date[key[1]](value)
				} else {
					col = moment.unix(value).locale('sv').fromNow()
				}
				break
			case 'label':
				col = (
					<span key={`table_row_${x}_${y}_key_${key}`} class={`badge bg-${key[1]} ${key[2] ? key[2] : ''}`}>
						{value}
					</span>
				)
				break
			case 'button':
				col = (
					<button
						key={`table_row_${x}_${y}_key_${key}`}
						type='button'
						onClick={e => {
							e.stopPropagation()
							props.functions[key[1]](row)
						}}
						class='btn btn-sm btn-light table-icon-action'
						aria-label={getActionLabel(key[1], row, key[3])}>
						<i class={`fas fa-fw ${key[2]}`} aria-hidden="true" />
					</button>
				)
				break
			case 'buttons':
				var buttons = []
				for (let index = 1; index < key.length; index = index + 2) {
					buttons.push(
						<button
							key={`table_row_${x + index}_${y + index}_key_${key}`}
							type='button'
							onClick={e => {
								e.stopPropagation()
								props.functions[key[index]](row)
							}}
							class={`btn btn-sm btn-light table-icon-action ${key.length !== index + 2 ? 'mr-10' : ''}`}
							aria-label={getActionLabel(key[index], row)}>
							<i class={`fas fa-fw ${key[index + 1]}`} aria-hidden="true" />
						</button>
					)
				}
				col = buttons
				break
			case 'function':
				if (props.functions.hasOwnProperty(key[1])) {
					col = props.functions[key[1]](value)
				}
				break
			case 'currency':
				if (value === null) value = 0
				col = cashmoneyhelper.format(value)
				break
			default:
				col = `${value}${key[0]}`
		}

		return col
	}

	const keyLoop = function(row, key, x, y) {
		key = key.split('>')
		let col = key[0] === '_this' ? row : getValueFromPath(row, key[0])
		if (key.length > 1) {
			let operators = key[1].split('+')
			operators.forEach(operator => (col = parseKey(col, operator.split(':'), row, x, y)))
			if (operators.includes('nullable') && getValueFromPath(row, key[0]) === null) col = ''
		}
		return col
	}
	const sort = header => {
		if (!header.sortable) return
		props.useSorting({
			'sort[by]': header.key,
			'sort[order]': props.sorting['sort[order]'] === 'asc' ? 'desc' : 'asc'
		})
	}
	const onSortKeyDown = (e, header) => {
		if (!header.sortable) return
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault()
			sort(header)
		}
	}
	if (props?.rows?.length === 0) return <NoContentBox text={props.empty?.text || 'Det finns ingen data'} icon={props.empty?.icon || 'fa-info-circle'} />
	if (props.fetching || props.rows === undefined) return <TableLoader rows={props.loadercount || 3} />

	const isActionKey = keyStr => />(button|buttons|link):/.test(keyStr)
	const firstLinkColIndex = (() => {
		if (!rowKeys) return 0
		const keys = rowKeys.split('|')
		for (let i = 0; i < keys.length; i++) {
			if (props.headers?.[i]?.hidden) continue
			if (isActionKey(keys[i])) continue
			return i
		}
		return -1
	})()

	return (
		<div class='component_Table table-responsive'>
			<table class={`table align-middle table-nowrap mb-0`}>
				<thead>
					<tr>
						{props.headers &&
							props.headers.map((header, x) => {
								if (header.hidden) return
								return (
									<th
										key={`table_head_${x}`}
										onClick={() => sort(header)}
										onKeyDown={e => onSortKeyDown(e, header)}
										role={header.sortable ? 'button' : undefined}
										tabIndex={header.sortable ? 0 : undefined}
										aria-sort={
											header.sortable && props.sorting['sort[by]'] === header.key
												? props.sorting['sort[order]'] === 'asc'
													? 'ascending'
													: 'descending'
												: undefined
										}
										class={(header.sortable ? 'sortable-table-head ' : '') + (header.class ? header.class : '')}>
										<span>
											{header.value}
											{props.sorting['sort[by]'] === undefined && header.key === 'created_at' ? (
												<i class='fas fa-sort-alpha-up-alt sort-icon' aria-hidden="true" />
											) : header.sortable && props.sorting['sort[by]'] === header.key ? (
												props.sorting['sort[order]'] === 'asc' ? (
													<i class='fas fa-sort-alpha-down sort-icon' aria-hidden="true" />
												) : (
													<i class='fas fa-sort-alpha-up-alt sort-icon' aria-hidden="true" />
												)
											) : (
												''
											)}
										</span>
									</th>
								)
							})}
						{props.children}
					</tr>
				</thead>
				<tbody>
					{props.rows &&
						props.rows.map((row, x) => {
							if (rowKeys) {
								var keys = rowKeys.split('|')
								return (
									<tr key={`table_row_${x}`} onClick={e => onRowClick(row)} class={`${props.link ? 'pointer' : ''}`}>
										{keys.map((key, y) => {
											if (props.headers && props.headers[y].hidden) return
											key = key.split('&')
											return (() => {
												const cellContent = key.map(key => keyLoop(row, key, x, y))
												const header = props.headers?.[y]
												const isArrayEmpty = Array.isArray(cellContent) && cellContent.every(v => v === null || v === undefined || v === '')
												const isEmpty = cellContent === null || cellContent === undefined || cellContent === '' || isArrayEmpty
												const displayValue = isEmpty && header?.ifnull ? header.ifnull : cellContent
												const isLinkCell = props.link && y === firstLinkColIndex
												return (
													<td key={`table_row_${x}_col_${y}`}>
														{isLinkCell ? (
															<a
																href={getLinkHref(row, props.link)}
																class='table-row-link'
																onClick={e => e.stopPropagation()}
																aria-label={`Öppna ${getRowLabel(row)}`}>
																{displayValue}
															</a>
														) : (
															displayValue
														)}
													</td>
												)
											})()
										})}
									</tr>
								)
							}
							return (
								<tr key={`table_row_${x}`}>
									{Object.values(row).map((value, y) => (
										<td key={`table_row_${x}_col_${y}`}>{value}</td>
									))}
								</tr>
							)
						})}
				</tbody>
			</table>
		</div>
	)
}
